"use client";
import Identity from './Identity';
import { getLastUsedEmail } from '../models/common';
import { useActionState, useEffect, useRef, useState } from 'react';
import { handleLogin } from './actions';
import { avenirNextDemi } from '@/styles/fonts';
import clsx from 'clsx';
import { AdbeatButton } from '../components/adbeatButton';
import TurnstileWidget, { type TurnstileWidgetHandle } from '../components/TurnstileWidget.client';

const labelClassName = clsx(
    avenirNextDemi.className,
    "text-[#c0cad1]",
    "text-[12px]",
    "font-normal",
    "uppercase",
);

const inputClassName = clsx(
    "h-[46px]",
    avenirNextDemi.className,
    "text-[15px]",
    "text-gray-800",
    "w-full",
    "mb-[10px]",
    "appearance-none",
    "bg-white",
    "border",
    "border-[#f2f5f9]",
    "rounded-[5px]",
    "px-[15px]",
    "box-border",
    "shadow-[inset_0_0px_1px_#f2f5f9]",
);

const initialState = {
    error: "",
};

export default function LoginForm(props: {
    email?: string,
    identity: string,
    returnTo?: string,
    isCaptchaEnabled?: boolean,
}) {
    const email = props.email || getLastUsedEmail();
    const emailRef = useRef<HTMLInputElement>(null);
    const [state, formAction] = useActionState(handleLogin, initialState);
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileError, setTurnstileError] = useState('');
    const turnstileRef = useRef<TurnstileWidgetHandle>(null);
    const { error } = state;

    // Turnstile tokens are single-use. After any failed login (server returns
    // an error), the previous token has already been consumed. Reset the
    // widget so the user gets a fresh token before resubmitting.
    useEffect(() => {
        if (error) {
            turnstileRef.current?.reset();
            setTurnstileToken('');
        }
    }, [error]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        const emailVal = emailRef.current?.value.toLowerCase() ?? "";
        if (props.isCaptchaEnabled && !turnstileToken && !emailVal.endsWith("@adbeat.com")) {
            event.preventDefault();
            setTurnstileError('Please complete the captcha verification before submitting.');
        }
    };

    return (
        <>
            {error &&
                <div className={clsx(
                    "text-center",
                    "text-red-500",
                    "mb-[10px]",
                )}>{error}</div>}
            <form action={formAction} onSubmit={handleSubmit} suppressHydrationWarning>
                <div className="mb-[10px]">
                    <label className={labelClassName}>Email</label>
                    <input id="login_email" type="text" defaultValue={email} name="email" placeholder="Email"
                        ref={emailRef}
                        autoComplete='username'
                        className={inputClassName}
                    />
                </div>
                <div className="mb-[10px]">
                    <label className={labelClassName}>Password</label>

                    <input id="login_password" type="password" defaultValue="" name="password" placeholder="Password"
                        autoComplete='current-password'
                        className={inputClassName}
                    />
                </div>
                <Identity identity={props.identity} />
                <input type="hidden" name="return" value={props.returnTo || ''} />
                <input type="hidden" name="cf-turnstile-response" value={turnstileToken} />
                {props.isCaptchaEnabled && (
                    <div className="mb-[10px]">
                        <TurnstileWidget
                            ref={turnstileRef}
                            action="login"
                            onTokenChange={(token) => {
                                setTurnstileToken(token);
                                const emailVal = emailRef.current?.value.toLowerCase() ?? "";
                                if (token || emailVal.endsWith('@adbeat.com')) {
                                    setTurnstileError('');
                                }
                            }}
                        />
                        {turnstileError && (
                            <small className="text-red-500 block mt-1">{turnstileError}</small>
                        )}
                    </div>
                )}
                <AdbeatButton
                    type="submit"
                    text="Sign In"
                    name='login'
                    className={clsx(
                        "sm:w-full",
                        'bg-gradient-to-b',
                        'border-[#2da1f8]',
                        'from-[#2da1f8]',
                        'to-[#1991ed]',
                        'text-[16px]',
                        'min-w-[192px]',
                        "mx-auto",
                    )}
                />
            </form>
        </>
    );
}

